import React, { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";
import {
  LayoutDashboard,
  Users,
  Package,
  FileText,
  Briefcase,
  ShoppingBag,
  Globe,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Menu,
  X,
} from "lucide-react";

function Glyph({ className = "" }) {
  return (
    <svg viewBox="0 0 64 64" className={className} aria-hidden>
      <defs>
        <linearGradient id="gx-side-g" x1="0" x2="1" y1="0" y2="1">
          <stop offset="0%" stopColor="#f4d57f" />
          <stop offset="100%" stopColor="#9a7a1d" />
        </linearGradient>
      </defs>
      <circle cx="32" cy="32" r="30" fill="none" stroke="url(#gx-side-g)" strokeWidth="1" opacity="0.7" />
      <path
        d="M32 10 C 22 18, 18 28, 22 38 C 26 48, 38 48, 42 38 C 46 28, 42 18, 32 10 Z"
        fill="url(#gx-side-g)"
        opacity="0.92"
      />
      <circle cx="32" cy="32" r="2.4" fill="#0a0a0d" />
    </svg>
  );
}

const NAV = [
  { to: "/admin-dashboard", label: "Overview", icon: LayoutDashboard, end: true, key: "overview" },
  { to: "/admin-dashboard/crm", label: "CRM", icon: Users, key: "crm" },
  { to: "/admin-dashboard/inventory", label: "Inventory", icon: Package, key: "inventory" },
  { to: "/admin-dashboard/shop", label: "Shop", icon: ShoppingBag, key: "shop" },
  { to: "/admin-dashboard/quotations", label: "Quotations", icon: FileText, key: "quotations" },
  { to: "/admin-dashboard/projects", label: "Projects", icon: Briefcase, key: "projects" },
  { to: "/admin-dashboard/website-content", label: "Website Content", icon: Globe, key: "website-content" },
];

const ACTIVE_TITLE = {
  "/admin-dashboard": "Overview",
  "/admin-dashboard/crm": "CRM",
  "/admin-dashboard/inventory": "Inventory",
  "/admin-dashboard/shop": "Shop",
  "/admin-dashboard/quotations": "Quotations",
  "/admin-dashboard/projects": "Projects",
  "/admin-dashboard/website-content": "Website Content",
};

export default function AdminShell({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    setMobileOpen((v) => (v === false ? v : false));
  }, [loc.pathname]);

  const onLogout = async () => {
    await logout();
    nav("/admin-dashboard/login", { replace: true });
  };

  const currentTitle = (() => {
    const exact = ACTIVE_TITLE[loc.pathname];
    if (exact) return exact;
    const seg = loc.pathname.split("/").filter(Boolean)[1];
    return seg ? seg.charAt(0).toUpperCase() + seg.slice(1) : "Business OS";
  })();

  const SidebarBody = (
    <>
      <div className="flex items-center gap-3 px-5 pt-6 pb-5" style={{ borderBottom: "1px solid var(--ax-border)" }}>
        <Glyph className="w-8 h-8 shrink-0" />
        {!collapsed && (
          <div>
            <div className="ax-gold-text font-semibold tracking-[0.32em] text-[10.5px]">GENIEX</div>
            <div className="text-[10px] tracking-[0.28em] uppercase" style={{ color: "var(--ax-text-muted)" }}>
              Business OS
            </div>
          </div>
        )}
      </div>

      <nav className="flex-1 p-3 space-y-1 ax-scroll overflow-y-auto">
        {NAV.map(({ to, label, icon: Icon, end, key }) => (
          <NavLink
            key={key}
            to={to}
            end={end}
            data-testid={`nav-${key}`}
            className={({ isActive }) => `ax-side-link ${isActive ? "active" : ""}`}
            style={collapsed ? { justifyContent: "center", paddingLeft: 14 } : undefined}
            title={collapsed ? label : undefined}
          >
            <Icon className="w-4 h-4 shrink-0" />
            {!collapsed && <span>{label}</span>}
          </NavLink>
        ))}
      </nav>

      <div className="p-3 space-y-2" style={{ borderTop: "1px solid var(--ax-border)" }}>
        {!collapsed && user && (
          <div className="px-3 py-2 rounded-lg" style={{ background: "rgba(255,255,255,0.02)" }}>
            <div className="text-[10.5px] tracking-[0.18em] uppercase" style={{ color: "var(--ax-text-muted)" }}>
              Signed in
            </div>
            <div className="text-sm font-medium mt-1" data-testid="admin-user-name">
              {user.name || user.username}
            </div>
          </div>
        )}
        <button
          type="button"
          onClick={onLogout}
          data-testid="admin-logout"
          className="ax-side-link w-full"
          style={collapsed ? { justifyContent: "center", paddingLeft: 14 } : undefined}
          title={collapsed ? "Logout" : undefined}
        >
          <LogOut className="w-4 h-4 shrink-0" />
          {!collapsed && <span>Logout</span>}
        </button>
        <button
          type="button"
          onClick={() => setCollapsed((v) => !v)}
          data-testid="admin-sidebar-toggle"
          className="ax-side-link w-full hidden lg:flex"
          style={collapsed ? { justifyContent: "center", paddingLeft: 14 } : undefined}
        >
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          {!collapsed && <span>Collapse</span>}
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen w-full flex" data-testid="admin-shell">
      {/* DESKTOP SIDEBAR — hidden below lg */}
      <aside
        data-testid="admin-sidebar"
        className="h-screen sticky top-0 flex-col shrink-0 hidden lg:flex"
        style={{
          width: collapsed ? 76 : 244,
          borderRight: "1px solid var(--ax-border)",
          background: "rgba(12,12,16,0.78)",
          backdropFilter: "blur(18px) saturate(140%)",
          WebkitBackdropFilter: "blur(18px) saturate(140%)",
          transition: "width 200ms ease",
        }}
      >
        {SidebarBody}
      </aside>

      {/* MOBILE DRAWER */}
      <div
        className={`lg:hidden fixed inset-0 z-[80] transition-opacity ${
          mobileOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        data-testid="admin-mobile-drawer"
      >
        <div
          className="absolute inset-0 bg-black/70"
          onClick={() => setMobileOpen(false)}
        />
        <aside
          className={`absolute left-0 top-0 bottom-0 w-[280px] flex flex-col transition-transform duration-300 ${
            mobileOpen ? "translate-x-0" : "-translate-x-full"
          }`}
          style={{
            borderRight: "1px solid var(--ax-border)",
            background: "rgba(10,10,14,0.98)",
            backdropFilter: "blur(18px) saturate(140%)",
          }}
        >
          {SidebarBody}
        </aside>
      </div>

      <main className="flex-1 min-w-0 overflow-x-hidden">
        {/* MOBILE TOP BAR */}
        <div
          className="lg:hidden sticky top-0 z-[60] flex items-center gap-3 px-4 h-14"
          style={{
            background: "rgba(10,10,14,0.92)",
            backdropFilter: "blur(14px)",
            borderBottom: "1px solid var(--ax-border)",
          }}
          data-testid="admin-mobile-topbar"
        >
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
            data-testid="admin-mobile-menu-toggle"
            className="w-10 h-10 rounded-lg flex items-center justify-center text-white"
            style={{ border: "1px solid var(--ax-border)" }}
          >
            {mobileOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          </button>
          <div className="flex items-center gap-2">
            <Glyph className="w-6 h-6" />
            <span className="text-sm font-medium tracking-wide text-white">{currentTitle}</span>
          </div>
        </div>

        {children}
      </main>
    </div>
  );
}
